import {RoundToken} from "@/tabletop/tokens/RoundToken.tsx";
import {Group} from "react-konva";
import {RoundTokenAppearance} from "./RoundTokenAppearance.tsx";
import {useTurn} from "@/encounter/turns/TurnHooks.ts";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {useDungeon} from "@/encounter/dungeon/DungeonHooks.ts";
import {combatantsOnLevel} from "./CombatantLevel.ts";
import {playerTokenVisibility} from "./PlayerTokenVisibility.ts";
import type {Visibility} from "@/tabletop/visibility/VisibilityModel.ts";
import type {WorldBounds} from "@/tabletop/visibility/ViewportCulling.ts";
import {circleIntersectsBounds} from "@/tabletop/visibility/ViewportCulling.ts";
import {CharacterSizes} from "@/characters/model/CharacterSizes.ts";
import {tokenNameplateLayout} from "./TokenNameplate.ts";
import {useTokenPresentation} from "./useTokenPresentation.ts";

interface TokenLayerProps {
    playerView?: boolean;
    visibility?: Visibility;
    viewportBounds?: WorldBounds;
}

export default function TokenLayer({playerView = false, visibility, viewportBounds}: TokenLayerProps) {
    const {isActiveCombatant} = useTurn();
    const {combatants} = useCombatants()
    const {dungeonSettings} = useDungeon();
    const {marks, statuses} = useTokenPresentation(visibility);
    const visibleCombatants = combatantsOnLevel(combatants, dungeonSettings.levelId)
        .map(combatant => ({combatant, status: statuses.get(combatant.id) ?? playerTokenVisibility(combatant, visibility)}))
        .filter(({status}) => !playerView || status.visible)
        .filter(({combatant}) => {
            if (!viewportBounds) return true;
            const size = CharacterSizes.get(combatant.character.size) ?? 0;
            const label = tokenNameplateLayout(combatant.name, size);
            const extent = Math.max(size * 0.68 + 16, label.top + label.height + 30,
                                    label.width / 2, 80);
            return circleIntersectsBounds(combatant.position, extent, viewportBounds);
        });
    return (<>
            {visibleCombatants.map(({combatant, status}) =>
                playerView ? <Group key={combatant.id} x={combatant.position.x} y={combatant.position.y} listening={false}>
                    <RoundTokenAppearance combatant={combatant} playerView active={isActiveCombatant(combatant)}
                        areaEffectMarks={marks[combatant.id] ?? []}/>
                </Group> : <RoundToken
                    key={combatant.id}
                    combatant={combatant}
                    outsidePlayerSight={status.outsideSight}
                    areaEffectMarks={marks[combatant.id] ?? []}
                />)}
        </>
    )
}
